import React, { useState, useEffect } from 'react';

interface LoadingAnimationProps {
  url?: string;
}

const LoadingAnimation: React.FC<LoadingAnimationProps> = ({ url }) => {
  const [stage, setStage] = useState(0);
  const [factIndex, setFactIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [startTime] = useState(Date.now());
  const [isSlowTest, setIsSlowTest] = useState(false);

  const stages = [
    { icon: '🌐', text: 'Navigating to website...', duration: 2000 },
    { icon: '📋', text: 'Loading page content...', duration: 2500 },
    { icon: '🔍', text: 'Analyzing HTML structure...', duration: 3000 },
    { icon: '✅', text: 'Checking WCAG compliance...', duration: 4000 },
    { icon: '📊', text: 'Calculating accessibility score...', duration: 2000 }
  ];

  const accessibilityFacts = [
    "Did you know? 15% of the global population experiences some form of disability.",
    "Screen readers can read up to 300 words per minute for experienced users!",
    "The first web accessibility guidelines were published in 1995.",
    "Alt text was originally created for slow internet connections, now it's essential for screen readers.",
    "Color blindness affects 1 in 12 men and 1 in 200 women globally.",
    "Keyboard navigation helps not just disabled users, but also power users who prefer shortcuts.",
    "WCAG 2.1 added 17 new success criteria focusing on mobile accessibility.",
    "Captions benefit not just deaf users, but also help in noisy environments or when learning languages.",
    "The average lawsuit for web accessibility violations costs companies $25,000-$50,000.",
    "Accessible websites often have better SEO since search engines use similar parsing techniques as screen readers.",
    "Voice control users navigate websites using numbered overlays on interactive elements.",
    "High contrast mode is used by people with low vision and also reduces eye strain in bright environments.",
    "The tab key should move focus through interactive elements in a logical order.",
    "ARIA was introduced in 2014 to make dynamic content accessible.",
    "PDF documents can be made accessible with proper tagging and structure.",
  ];

  useEffect(() => {
    // Progress animation - more realistic progression
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        // const elapsed = Date.now() - startTime;

        // Slower progress as we get closer to completion
        if (prev >= 99) return 99; // Stop at 99% until actually complete
        if (prev >= 90) return prev + Math.random() * 0.5; // Very slow after 90%
        if (prev >= 70) return prev + Math.random() * 1; // Slow after 70%
        if (prev >= 50) return prev + Math.random() * 2; // Medium speed
        return prev + Math.random() * 3; // Normal speed at start
      });
    }, 300);

    // Check if test is taking longer than expected (>30 seconds)
    const slowTestTimeout = setTimeout(() => {
      setIsSlowTest(true);
    }, 30000);

    // Stage progression
    const stageInterval = setInterval(() => {
      setStage(prev => (prev + 1) % stages.length);
    }, 3000);

    // Fact rotation
    const factInterval = setInterval(() => {
      setFactIndex(prev => (prev + 1) % accessibilityFacts.length);
    }, 4000);

    return () => {
      clearInterval(progressInterval);
      clearInterval(stageInterval);
      clearInterval(factInterval);
      clearTimeout(slowTestTimeout);
    };
  }, [startTime]);

  const currentStage = stages[stage];
  const currentFact = accessibilityFacts[factIndex];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
      <div className="space-y-6">
        {/* Animated Icon Row */}
        <div className="flex justify-center space-x-4">
          {stages.map((s, index) => (
            <div
              key={index}
              className={`transition-all duration-500 ${
                index === stage
                  ? 'text-4xl animate-bounce'
                  : index < stage
                  ? 'text-2xl opacity-50'
                  : 'text-2xl opacity-20'
              }`}
            >
              <span>{s.icon}</span>
            </div>
          ))}
        </div>

        {/* Current Stage Text */}
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 animate-pulse">
            {currentStage.text}
          </h3>
          {url && (
            <p className="text-sm text-gray-500 mt-1">
              Testing: {url}
            </p>
          )}
        </div>

        {/* Progress Bar */}
        <div className="relative">
          <div className="bg-gray-200 rounded-full h-3 overflow-hidden">
            <div
              className="bg-gradient-to-r from-blue-500 to-purple-600 h-full rounded-full transition-all duration-300 relative"
              style={{ width: `${progress}%` }}
            >
              {/* Shimmer effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer"></div>
            </div>
          </div>
          <div className="absolute -top-1 transition-all duration-300"
               style={{ left: `${Math.min(progress, 90)}%` }}>
            <div className="bg-white rounded-full p-1 shadow-lg">
              <div className="w-2 h-2 bg-purple-600 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>

        {/* Progress Percentage */}
        <div className="text-center">
          <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            {Math.round(progress)}%
          </span>
        </div>

        {/* Accessibility Fact */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 border border-blue-100">
          <div className="flex items-start space-x-3">
            <span className="text-2xl">💡</span>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-700">
                Accessibility Fact
              </p>
              <p className="text-sm text-gray-600 mt-1 italic">
                {currentFact}
              </p>
            </div>
          </div>
        </div>

        {/* Estimated Time */}
        <div className="text-center text-sm text-gray-500">
          <span className="inline-flex items-center space-x-2">
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
            </svg>
            <span>
              {progress >= 95 ? (
                'Finalizing results...'
              ) : isSlowTest ? (
                'Complex page detected, this may take a moment...'
              ) : (
                `Estimated time remaining: ${Math.max(3, Math.ceil((100 - progress) / 5))} seconds`
              )}
            </span>
          </span>
        </div>
      </div>

    </div>
  );
};

export default LoadingAnimation;
